import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Dropdown, Spin, Table, Tag } from "antd";
import { MailOutlined } from "@ant-design/icons";
import moment from "moment";
import { useSelector } from "react-redux";
import { protectedApi } from "../../../app/utils/common/protectedApi";
import { APIS } from "../../../app/utils/constants/apiConstant";
import {
  canReadAllPerformanceCriteria,
  canSendPerformanceCriteriaEmail,
  showErrorResponse,
} from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import "./PerformanceCriteriaPage.scss";

const formatName = (row) =>
  `${row?.firstname || ""} ${row?.lastname || ""}`.trim() || `User #${row?.userId}`;

const formatDate = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("D MMM YYYY") : String(value);
};

const formatTime = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("h:mm A") : "—";
};

const PerformanceCriteriaPage = () => {
  const user = useSelector((state) => state.auth?.auth?.user);
  const canViewAll = canReadAllPerformanceCriteria(user);
  const canSendEmail = canSendPerformanceCriteriaEmail(user);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => moment());
  const [report, setReport] = useState(null);
  const [sendingKey, setSendingKey] = useState(null);

  const monthParam = useMemo(() => month.format("YYYY-MM"), [month]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await protectedApi({
        url: APIS.PERFORMANCE_CRITERIA_LATE_ARRIVALS,
        method: "GET",
        params: { month: monthParam },
      });
      setReport(response?.data?.data ?? null);
    } catch (error) {
      showErrorResponse(error);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [monthParam]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSendEmail = async (row, emailType) => {
    if (!row?.userId || !row?.email) {
      showErrorToast("Employee has no email address on file.");
      return;
    }
    const key = `${row.userId}-${emailType}`;
    setSendingKey(key);
    try {
      const response = await protectedApi({
        url: APIS.PERFORMANCE_CRITERIA_SEND_EMAIL,
        method: "POST",
        data: {
          userId: row.userId,
          month: monthParam,
          emailType,
        },
      });
      showSuccessToast(response?.data?.message || "Email sent successfully.");
      await load();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setSendingKey(null);
    }
  };

  const policy = report?.policy || {
    warningAfterLateCount: 3,
    halfDayAfterLateCount: 4,
  };

  const summaryColumns = [
    {
      title: "Employee",
      key: "name",
      render: (_, row) => formatName(row),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => email || "—",
    },
    {
      title: "Late count",
      dataIndex: "lateCount",
      key: "lateCount",
      width: 110,
      render: (count) => {
        const n = Number(count) || 0;
        let color = "default";
        if (n >= policy.halfDayAfterLateCount) color = "red";
        else if (n >= policy.warningAfterLateCount) color = "orange";
        else if (n > 0) color = "gold";
        return <Tag color={color}>{n}</Tag>;
      },
    },
    {
      title: "Warning threshold",
      dataIndex: "warningsSent",
      key: "warningsSent",
      width: 140,
      render: (v) => Number(v) || 0,
    },
    {
      title: "Half days applied",
      dataIndex: "halfDaysApplied",
      key: "halfDaysApplied",
      width: 150,
      render: (v) => Number(v) || 0,
    },
    ...(canSendEmail
      ? [
          {
            title: "Actions",
            key: "actions",
            width: 140,
            render: (_, row) => {
              const lateCount = Number(row.lateCount) || 0;
              const canWarning = lateCount >= policy.warningAfterLateCount;
              const canHalfDay = lateCount >= policy.halfDayAfterLateCount;
              if (!canWarning && !canHalfDay) return "—";
              if (!row.email) {
                return <span className="performance-criteria-page__no-email">No email</span>;
              }

              const items = [];
              if (canWarning) {
                items.push({
                  key: "warning",
                  label: "Send warning email",
                  onClick: () => handleSendEmail(row, "warning"),
                });
              }
              if (canHalfDay) {
                items.push({
                  key: "half_day",
                  label: "Send half-day notice",
                  onClick: () => handleSendEmail(row, "half_day"),
                });
              }

              const busy =
                sendingKey === `${row.userId}-warning` ||
                sendingKey === `${row.userId}-half_day`;

              return (
                <Dropdown menu={{ items }} trigger={["click"]} disabled={busy}>
                  <Button
                    type="default"
                    size="small"
                    icon={<MailOutlined />}
                    loading={busy}
                  >
                    Send email
                  </Button>
                </Dropdown>
              );
            },
          },
        ]
      : []),
  ];

  const detailColumns = [
    ...(canViewAll
      ? [
          {
            title: "Employee",
            key: "employee",
            render: (_, row) => formatName(row),
          },
        ]
      : []),
    {
      title: "Date",
      dataIndex: "workDate",
      key: "workDate",
      render: (d) => formatDate(d),
    },
    {
      title: "Arrival time",
      dataIndex: "punchTime",
      key: "punchTime",
      render: (t) => formatTime(t),
    },
    {
      title: "3rd late",
      dataIndex: "warningSentAt",
      key: "warningSentAt",
      render: (v) => (v ? <Tag color="orange">Yes</Tag> : "—"),
    },
    {
      title: "Half day",
      dataIndex: "halfDayApplied",
      key: "halfDayApplied",
      render: (v) => (Number(v) ? <Tag color="red">Applied</Tag> : "—"),
    },
  ];

  const summaryData = canViewAll ? report?.summary || [] : [];
  const detailData = canViewAll
    ? report?.records || []
    : report?.records || [];

  const ownLateCount = canViewAll ? null : Number(report?.lateCount) || 0;

  return (
    <div className="performance-criteria-page">
      <div className="performance-criteria-page__header">
        <div>
          <h1 className="performance-criteria-page__title">Performance Criteria</h1>
          <p className="performance-criteria-page__subtitle">
            Late arrival policy: after {policy.warningAfterLateCount} late arrivals in a calendar month
            the employee reaches the warning threshold; on the {policy.halfDayAfterLateCount}th late
            arrival a half-day deduction is applied automatically. Late is defined as first arrival at
            or after 9:41 AM (before 1:00 PM). HR can send notice emails manually from the team
            summary table. Data is synced from attendance punch logs for the selected month.
          </p>
          {month.isAfter(moment(), "month") && (
            <p className="performance-criteria-page__hint">
              The selected month is in the future — switch to the current or a past month to see records.
            </p>
          )}
        </div>
        <DatePicker
          picker="month"
          value={month}
          onChange={(value) => value && setMonth(value)}
          allowClear={false}
          disabledDate={(current) => current && current.isAfter(moment(), "month")}
        />
      </div>

      {loading ? (
        <div className="performance-criteria-page__loading">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {!canViewAll && (
            <div className="performance-criteria-page__own-summary">
              <span className="label">Your late arrivals this month</span>
              <span className="value">{ownLateCount}</span>
              {report?.employeeName ? (
                <span className="name">{report.employeeName}</span>
              ) : null}
            </div>
          )}

          {canViewAll && (
            <section className="performance-criteria-page__section">
              <h2>Team summary</h2>
              <Table
                rowKey="userId"
                columns={summaryColumns}
                dataSource={summaryData}
                pagination={{ pageSize: 20, showSizeChanger: true }}
                locale={{ emptyText: "No late arrivals recorded for this month." }}
              />
            </section>
          )}

          <section className="performance-criteria-page__section">
            <h2>{canViewAll ? "Late arrival details" : "Your late arrivals"}</h2>
            <Table
              rowKey="id"
              columns={detailColumns}
              dataSource={detailData}
              pagination={{ pageSize: 15, showSizeChanger: true }}
              locale={{ emptyText: "No late arrivals recorded for this month." }}
            />
          </section>
        </>
      )}
    </div>
  );
};

export default PerformanceCriteriaPage;
